import HelpOutlineIcon from '@mui/icons-material/HelpOutline';
import RuleIcon from '@mui/icons-material/Rule';
import { Box, Button, IconButton, Stack, Tooltip, Typography } from '@mui/material';
import pluralize from 'pluralize';
import React, { FunctionComponent } from 'react';
import { CaseOrganizationExecutionMode } from './caseOrganizationPlan';
import { MANIFEST_TABLE_FIELD_GUIDANCE } from './manifestWorkbookInstructions';

interface ManifestDraftStatusProps {
  blockingErrorCount: number;
  caseCount: number;
  isOrganizationView: boolean;
  onReviewUnresolvedRows: () => void;
  executionMode?: CaseOrganizationExecutionMode;
  slideCount: number;
  unresolvedRowCount: number;
  warningCount: number;
}

const formatCount = (count: number, label: string) => pluralize(label, count, true);

export const ManifestDraftStatus: FunctionComponent<ManifestDraftStatusProps> = ({
  blockingErrorCount,
  caseCount,
  isOrganizationView,
  onReviewUnresolvedRows,
  executionMode,
  slideCount,
  unresolvedRowCount,
  warningCount,
}) => {
  const existingCaseHelp =
    executionMode === 'wholeStudyReplacement'
      ? 'Only case assignments in the plan are kept in this study. Cases whose labels are absent from the plan are deleted before the case batches are applied.'
      : executionMode === 'selectedCaseOverride'
      ? 'For each case label in the plan, only the slides in the plan are kept in that case. Cases not listed remain unless slides move out of them.'
      : executionMode === 'routine'
      ? 'For each case label in the plan, other slides in that case are kept. Change Existing case handling to replace case assignments.'
      : 'Choose a supported Existing case handling option before reviewing changes.';
  const guidance =
    slideCount === 0
      ? 'Select slides to start building the case plan.'
      : isOrganizationView
      ? 'Review case labels and slide metadata before reviewing changes.'
      : 'Add slides, then return to the case plan.';

  return (
    <Box
      aria-live="polite"
      data-testid="manifest-draft-status"
      role="status"
      sx={{ px: 2, py: 1.25, bgcolor: 'action.hover', borderTop: 1, borderColor: 'divider' }}
    >
      <Stack direction="row" alignItems="center" justifyContent="space-between" spacing={2}>
        <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" useFlexGap>
          <Typography variant="subtitle2" component="span">
            Case plan
          </Typography>
          <Typography variant="body2">{formatCount(caseCount, 'case')}</Typography>
          <Typography variant="body2">{formatCount(slideCount, 'slide')}</Typography>
          <Typography variant="body2" color={warningCount > 0 ? 'warning.main' : 'text.secondary'}>
            {formatCount(warningCount, 'warning')}
          </Typography>
          <Typography variant="body2" color={blockingErrorCount > 0 ? 'error' : 'text.secondary'}>
            {formatCount(blockingErrorCount, 'blocking error')}
          </Typography>
        </Stack>
        <Tooltip
          title={
            <Stack spacing={1}>
              <Typography variant="body2">Slides are grouped by case label.</Typography>
              <Typography variant="body2">
                Slides with the same label are combined into one case. A label already in use in this study updates that
                case; a new label creates a case.
              </Typography>
              {MANIFEST_TABLE_FIELD_GUIDANCE.map((guidanceText) => (
                <Typography key={guidanceText} variant="body2">
                  {guidanceText}
                </Typography>
              ))}
              <Typography variant="body2">{existingCaseHelp}</Typography>
            </Stack>
          }
          componentsProps={{ tooltip: { sx: { maxWidth: 320, p: 1.5 } } }}
          describeChild
          arrow
        >
          <IconButton size="small" aria-label="About case assignment">
            <HelpOutlineIcon fontSize="small" />
          </IconButton>
        </Tooltip>
      </Stack>
      {unresolvedRowCount > 0 && (
        <Stack direction="row" alignItems="center" spacing={2} sx={{ mt: 0.5 }}>
          <Typography variant="body2">{formatCount(unresolvedRowCount, 'unresolved row')}</Typography>
          <Button size="small" variant="text" color="inherit" startIcon={<RuleIcon />} onClick={onReviewUnresolvedRows}>
            Review unresolved rows
          </Button>
        </Stack>
      )}
      <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
        {guidance}
      </Typography>
    </Box>
  );
};
